import clsx from "clsx";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import Agent from "../pages/Agent";
import MessageTemplates from "../pages/message-templates";
import CreateChase from "../pages/CreateChase";
import ChaseList from "../pages/ChaseList";
import Dashboard from "../pages/Dashboard";
import LeftSidebar from "./LeftSidebar";
import { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from "@fortawesome/free-solid-svg-icons";
import { useSidebar } from "../store/use-sidebar";
import BrandedLogo from "./branded-logo";
import Auth from "../pages/Auth";
import ResetPassword from "../pages/ResetPassword";
import GraphProvider from "../providers/graphs-provider";
import LowScreen from "./low-screen";
import AgentCodeSetupAlert from "./AgentCodeSetupAlert";

function MainComponent() {
  const location = useLocation();
  const collapsed = useSidebar((s) => s.collapsed);
  const mobileOpen = useSidebar((s) => s.mobileOpen);
  const setMobileOpen = useSidebar((s) => s.setMobileOpen);
  const hideSideBarRoutes = [
    "createChase",
    "createInsurer",
    "auth",
    "reset",
    "chat",
  ];
  const showSidebar = !hideSideBarRoutes?.includes(
    location.pathname.split("/")[1]
  );

  // The mobile drawer closes on every navigation.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  // Lock page scroll behind the open drawer.
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  return (
    <GraphProvider>
      <AgentCodeSetupAlert />
      <LowScreen />
      <div className="flex justify-between min-h-screen bg-background flex-grow">
        {showSidebar ? (
          <>
            {/* Below lg the sidebar is an off-canvas drawer over a scrim. */}
            <div
              onClick={() => setMobileOpen(false)}
              className={clsx(
                "lg:hidden fixed inset-0 z-[998] bg-[color:var(--shadow-modal-scrim)] backdrop-blur-sm transition-opacity duration-300",
                mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"
              )}
            />
            <div
              className={clsx(
                "shrink-0 transition-[width] duration-300 ease-out",
                collapsed ? "w-[76px]" : "w-[258px]",
                "max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:z-[999] max-lg:w-[280px] max-lg:max-w-[85vw] max-lg:transition-transform",
                mobileOpen ? "max-lg:translate-x-0" : "max-lg:-translate-x-full"
              )}
            >
              <LeftSidebar />
            </div>
          </>
        ) : null}
        <div
          className={clsx(
            "flex-1 min-w-0 min-h-screen h-full bg-background"
          )}
        >
          {showSidebar ? (
            <div className="lg:hidden sticky top-0 z-[50] flex items-center gap-3 h-14 px-4 bg-surface border-b border-edge">
              <button
                type="button"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-edge bg-overlay text-light-gray hover:text-text-primary transition-colors duration-200 cursor-pointer"
              >
                <FontAwesomeIcon icon={faBars} className="text-sm" />
              </button>
              <BrandedLogo className="w-[96px] shrink-0 opacity-95" />
            </div>
          ) : null}
          <Routes>
            <Route
              path="/"
              element={
                <GraphProvider>
                  <Dashboard />
                </GraphProvider>
              }
            />
            {/* The beta dashboard is now the only dashboard — keep the old path
                working for anyone who bookmarked it during the beta. */}
            <Route
              path="/dashboard-beta"
              element={<Navigate to="/" replace />}
            />
            <Route path="/chaselist" element={<ChaseList />} />
            <Route path="/chase/:id" element={<ChaseList />} />
            <Route path="/my-customers" element={<ChaseList />} />
            <Route path="/customer-archives" element={<ChaseList />} />

            <Route path="/createChase" element={<CreateChase />} />
            <Route path="/agents" element={<Agent />} />
            <Route path="/message-templates" element={<MessageTemplates />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/reset" element={<ResetPassword />} />
          </Routes>
        </div>
      </div>
    </GraphProvider>
  );
}

export default MainComponent;
